import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Container, Card, Row, Col, Form, Button, Alert, Table, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ProductImage from '../components/ProductImage/ProductImage';
import ViewToggle from '../components/ViewToggle/ViewToggle';
import Loading from '../components/Loading/Loading';
import EmptyState from '../components/EmptyState/EmptyState';
import DatePicker from '../components/DatePicker/DatePicker';
import styles from './LojaFisica/LojaFisicaCatalog.module.css';
import filterStyles from './RelatorioEntradas.module.css';

// Configurar API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://dashboardgringao.cloud/api';

const RelatorioEntradasEcommerce = () => {
    // Estado para datas - Default: Começo do mês atual e Data atual
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDateInput = (date) => date.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(formatDateInput(firstDay));
    const [endDate, setEndDate] = useState(formatDateInput(today));

    // Filtros avançados
    const [categoria, setCategoria] = useState('');
    const [fornecedoresSelecionados, setFornecedoresSelecionados] = useState([]);
    const [fornecedorDropdownOpen, setFornecedorDropdownOpen] = useState(false);
    const fornecedorDropdownRef = useRef(null);
    const [categorias, setCategorias] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);

    const [produtos, setProdutos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });
    const [viewMode, setViewMode] = useState('grid');
    const [stats, setStats] = useState({
        totalProdutos: 0,
        valorCustoTotal: 0,
        valorVendaTotal: 0,
        lucroTotal: 0,
        quantidadeEstoque: 0,
        quantidadeVendida: 0,
        quantidadeChegada: 0
    });

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (fornecedorDropdownRef.current && !fornecedorDropdownRef.current.contains(event.target)) {
                setFornecedorDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Buscar opções de filtros ao carregar
    useEffect(() => {
        const fetchFiltros = async () => {
            try {
                const response = await axios.get(`${API_URL}/relatorios/entradas-ecommerce/filtros`);
                if (response.data.success) {
                    setCategorias(response.data.categorias || []);
                    setFornecedores(response.data.fornecedores || []);
                }
            } catch (error) {
                console.error('Erro ao buscar filtros:', error);
            }
        };
        fetchFiltros();
    }, []);

    // Handler para toggle de fornecedor
    const handleFornecedorToggle = (fornecedor) => {
        setFornecedoresSelecionados(prev =>
            prev.includes(fornecedor)
                ? prev.filter(f => f !== fornecedor)
                : [...prev, fornecedor]
        );
    };

    const fetchRelatorio = async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        setMsg({ type: '', text: '' });

        try {
            const params = { startDate, endDate };
            if (categoria) params.categoria = categoria;
            // Enviar fornecedores como string separada por vírgula
            if (fornecedoresSelecionados.length > 0) {
                params.fornecedor = fornecedoresSelecionados.join(',');
            }
            // Tenta primeiro buscar da API Bling (tempo real)
            // Se falhar, usa o endpoint que busca do MySQL cache
            let response;
            try {
                response = await axios.get(`${API_URL}/relatorios/entradas-ecommerce-bling`, { params });
            } catch (blingError) {
                console.log('Fallback para cache MySQL...', blingError.message);
                response = await axios.get(`${API_URL}/relatorios/entradas-ecommerce`, { params });
            }

            if (response.data.success) {
                const data = response.data.data;
                setProdutos(data);

                // Calcular estatísticas
                const totalProd = data.length;
                const custoTotal = data.reduce((acc, p) => acc + (p.precoCusto * p.qtdChegada), 0);
                const vendaTotal = data.reduce((acc, p) => acc + (p.precoVenda * p.qtdChegada), 0);
                const lucroTotal = vendaTotal - custoTotal;
                const estoqueTotal = data.reduce((acc, p) => acc + (p.estoqueAtual || 0), 0);
                const vendidaTotal = data.reduce((acc, p) => acc + (p.qtdVendida || 0), 0);
                const chegadaTotal = data.reduce((acc, p) => acc + (p.qtdChegada || 0), 0);

                setStats({
                    totalProdutos: totalProd,
                    valorCustoTotal: custoTotal,
                    valorVendaTotal: vendaTotal,
                    lucroTotal: lucroTotal,
                    quantidadeEstoque: estoqueTotal,
                    quantidadeVendida: vendidaTotal,
                    quantidadeChegada: chegadaTotal
                });

                if (totalProd === 0) setMsg({ type: 'info', text: 'Nenhum produto encontrado no período.' });
                else setMsg({ type: 'success', text: `${totalProd} produtos carregados! Ordenados por mais vendidos.` });
            }
        } catch (error) {
            console.error(error);
            setMsg({ type: 'error', text: 'Erro ao buscar relatório. Verifique o console.' });
        } finally {
            setLoading(false);
        }
    };

    // Formatar moeda
    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
    };

    // Formatar data
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            // Se já vier no formato YYYY-MM-DD, formata manualmente para evitar timezone UTC para Local (dia anterior)
            if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [ano, mes, dia] = dateStr.split('-');
                return `${dia}/${mes}/${ano}`;
            }
            return new Date(dateStr).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
        } catch {
            return 'N/A';
        }
    };

    // Card de produto customizado para este relatório
    const EntradaCard = ({ produto }) => {
        const cardStyle = {
            height: '420px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        };

        const imageContainerStyle = {
            height: '140px',
            minHeight: '140px',
            maxHeight: '140px',
            position: 'relative',
            overflow: 'hidden'
        };

        const bodyStyle = {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '8px',
            overflow: 'hidden'
        };

        return (
            <Link to={`/ecommerce/${produto.codigo}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className={styles.card} style={cardStyle}>
                    <div className={styles.imageContainer} style={imageContainerStyle}>
                        <ProductImage
                            src={produto.imagemUrl}
                            alt={produto.nome}
                            className={styles.image}
                        />
                        {/* Tags */}
                        <div style={{
                            position: 'absolute',
                            top: '4px',
                            left: '4px',
                            right: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '3px'
                        }}>
                            {/* Data de Entrada */}
                            <Badge bg="info" className="shadow" style={{ fontSize: '0.7rem', padding: '4px 6px' }}>
                                📦 Entrada: {formatDate(produto.dataEntrada || produto.dataCadastro)}
                            </Badge>

                            {/* Total de vendas */}
                            {produto.totalVendas > 0 && (
                                <Badge bg="success" className="shadow" style={{ fontSize: '0.7rem' }}>
                                    🛒 Total vendido: {produto.totalVendas}
                                </Badge>
                            )}
                        </div>
                    </div>

                    <div style={bodyStyle}>
                        <div className={styles.code} style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>#{produto.codigo}</div>

                        <div style={{
                            height: '36px',
                            overflow: 'hidden',
                            fontSize: '0.8rem',
                            marginBottom: '4px'
                        }}>
                            {produto.nome?.substring(0, 50)}...
                        </div>

                        {/* Preços */}
                        <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: '0.8rem' }}>
                            <div>
                                <small className="text-muted d-block">Revenda</small>
                                <strong className="text-primary">{formatCurrency(produto.precoVenda)}</strong>
                            </div>
                            <div className="text-end">
                                <small className="text-muted d-block">Custo</small>
                                <strong className="text-secondary">{formatCurrency(produto.precoCusto)}</strong>
                            </div>
                        </div>

                        {/* Margem de Lucro */}
                        <div className="text-center mb-2 p-1 rounded" style={{
                            backgroundColor: parseFloat(produto.margemLucro) > 0 ? '#d4edda' : '#f8d7da',
                            fontSize: '0.8rem'
                        }}>
                            <small className="d-block">Margem</small>
                            <strong className={parseFloat(produto.margemLucro) > 0 ? 'text-success' : 'text-danger'}>
                                {produto.margemLucro}%
                            </strong>
                        </div>

                        {/* Quantidades */}
                        <div className="d-flex justify-content-between text-center" style={{ fontSize: '0.75rem' }}>
                            <div>
                                <div className="text-muted">Vendeu</div>
                                <strong className="text-success">{produto.qtdVendida}</strong>
                            </div>
                            <div>
                                <div className="text-muted">Estoque</div>
                                <strong className={produto.estoqueAtual > 0 ? 'text-primary' : 'text-danger'}>
                                    {produto.estoqueAtual}
                                </strong>
                            </div>
                            <div>
                                <div className="text-muted">Média/Mês</div>
                                <strong>{produto.mediaMensal?.toFixed(1) || 0}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    };

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0 text-gray-800">📦 Relatório de Entradas - E-commerce</h1>
                    <p className="mb-0 text-muted">Produtos do e-commerce - Ordenados por mais vendidos no período</p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
                </div>
            </div>

            {/* Filtros */}
            <Card className="shadow mb-4">
                <Card.Header className="py-3 bg-white">
                    <h6 className="m-0 font-weight-bold text-primary">Filtros</h6>
                </Card.Header>
                <Card.Body>
                    <Row className="align-items-end">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Data Inicial</Form.Label>
                                <DatePicker
                                    value={startDate}
                                    onChange={(valor) => setStartDate(valor)}
                                    placeholder="Data início"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Data Final</Form.Label>
                                <DatePicker
                                    value={endDate}
                                    onChange={(valor) => setEndDate(valor)}
                                    placeholder="Data fim"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Categoria</Form.Label>
                                <Form.Select
                                    value={categoria}
                                    onChange={(e) => setCategoria(e.target.value)}
                                >
                                    <option value="">Todas as Categorias</option>
                                    {categorias.map((cat, idx) => (
                                        <option key={idx} value={cat}>{cat}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Fornecedor</Form.Label>
                                <div ref={fornecedorDropdownRef} style={{ position: 'relative' }}>
                                    <div
                                        className={filterStyles.multiSelectTrigger}
                                        onClick={() => setFornecedorDropdownOpen(!fornecedorDropdownOpen)}
                                    >
                                        <span className={filterStyles.multiSelectText}>
                                            {fornecedoresSelecionados.length === 0
                                                ? 'Todos os Fornecedores'
                                                : fornecedoresSelecionados.length === 1
                                                    ? fornecedoresSelecionados[0]
                                                    : `${fornecedoresSelecionados.length} selecionados`}
                                        </span>
                                        <span>{fornecedorDropdownOpen ? '▲' : '▼'}</span>
                                    </div>
                                    {fornecedorDropdownOpen && (
                                        <div className={filterStyles.multiSelectDropdown}>
                                            <div className={filterStyles.multiSelectActions}>
                                                <button
                                                    type="button"
                                                    onClick={() => setFornecedoresSelecionados([...fornecedores])}
                                                    className={filterStyles.multiSelectActionBtn}
                                                >
                                                    Selecionar Todos
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFornecedoresSelecionados([])}
                                                    className={filterStyles.multiSelectActionBtn}
                                                >
                                                    Limpar
                                                </button>
                                            </div>
                                            <div className={filterStyles.multiSelectList}>
                                                {fornecedores.map((forn, idx) => (
                                                    <label key={idx} className={filterStyles.multiSelectItem}>
                                                        <input
                                                            type="checkbox"
                                                            checked={fornecedoresSelecionados.includes(forn)}
                                                            onChange={() => handleFornecedorToggle(forn)}
                                                        />
                                                        <span>{forn}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Button
                                variant="primary"
                                onClick={fetchRelatorio}
                                disabled={loading}
                                className="w-100"
                            >
                                {loading ? 'Buscando...' : 'Filtrar'}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Mensagens */}
            {msg.text && (
                <Alert
                    variant={msg.type === 'error' ? 'danger' : msg.type === 'success' ? 'success' : 'info'}
                    onClose={() => setMsg({ type: '', text: '' })}
                    dismissible
                >
                    {msg.text}
                </Alert>
            )}

            {/* Cards de Estatísticas */}
            {produtos.length > 0 && (
                <Row className="mb-4">
                    <Col xl={2} md={4} className="mb-3">
                        <Card className="border-left-primary shadow h-100 py-2">
                            <Card.Body className="py-2">
                                <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">Produtos</div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.totalProdutos}</div>
                                <small className="text-muted">modelos encontrados</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={2} md={4} className="mb-3">
                        <Card className="border-left-warning shadow h-100 py-2">
                            <Card.Body className="py-2">
                                <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">Vendas (Qtd)</div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.quantidadeVendida}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={2} md={4} className="mb-3">
                        <Card className="border-left-secondary shadow h-100 py-2">
                            <Card.Body className="py-2">
                                <div className="text-xs font-weight-bold text-secondary text-uppercase mb-1">Custo Total</div>
                                <div className="h6 mb-0 font-weight-bold text-gray-800">{formatCurrency(stats.valorCustoTotal)}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={2} md={4} className="mb-3">
                        <Card className="border-left-info shadow h-100 py-2">
                            <Card.Body className="py-2">
                                <div className="text-xs font-weight-bold text-info text-uppercase mb-1">Venda Total</div>
                                <div className="h6 mb-0 font-weight-bold text-gray-800">{formatCurrency(stats.valorVendaTotal)}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={2} md={4} className="mb-3">
                        <Card className="border-left-success shadow h-100 py-2">
                            <Card.Body className="py-2">
                                <div className="text-xs font-weight-bold text-success text-uppercase mb-1">Lucro Estimado</div>
                                <div className="h6 mb-0 font-weight-bold text-success">{formatCurrency(stats.lucroTotal)}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={2} md={4} className="mb-3">
                        <Card className="border-left-danger shadow h-100 py-2">
                            <Card.Body className="py-2">
                                <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">Saldo Estoque</div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.quantidadeEstoque}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Conteúdo Principal */}
            {loading && <Loading />}

            {!loading && produtos.length === 0 && !msg.text && (
                <EmptyState message="Utilize o filtro acima para buscar os produtos do e-commerce no período." />
            )}

            {!loading && produtos.length > 0 && (
                <Card className="shadow mb-4">
                    <Card.Header className="py-3 bg-white d-flex justify-content-between align-items-center">
                        <h6 className="m-0 font-weight-bold text-primary">Resultado da Busca</h6>
                        <small className="text-muted">Ordenado por mais vendidos primeiro</small>
                    </Card.Header>
                    <Card.Body className={viewMode === 'table' ? 'p-0' : 'p-3'}>
                        {viewMode === 'table' ? (
                            <div className="table-responsive">
                                <Table striped bordered hover size="sm">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Data Entrada</th>
                                            <th>Código</th>
                                            <th>Descrição</th>
                                            <th>Categoria</th>
                                            <th>Fornecedor</th>
                                            <th className="text-end">Custo</th>
                                            <th className="text-end">Revenda</th>
                                            <th className="text-end">Margem</th>
                                            <th className="text-center">Vendeu</th>
                                            <th className="text-center">Estoque</th>
                                            <th className="text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {produtos.map(p => (
                                            <tr key={p.codigo}>
                                                <td>{formatDate(p.dataEntrada || p.dataCadastro)}</td>
                                                <td>{p.codigo}</td>
                                                <td title={p.nome}>{p.nome?.substring(0, 30)}...</td>
                                                <td>{p.categoria}</td>
                                                <td>{p.fornecedor}</td>
                                                <td className="text-end">{formatCurrency(p.precoCusto)}</td>
                                                <td className="text-end">{formatCurrency(p.precoVenda)}</td>
                                                <td className={`text-end ${parseFloat(p.margemLucro) > 0 ? 'text-success' : 'text-danger'}`}>
                                                    {p.margemLucro}%
                                                </td>
                                                <td className="text-center fw-bold text-success">{p.qtdVendida}</td>
                                                <td className={`text-center ${p.estoqueAtual > 0 ? '' : 'text-danger'}`}>
                                                    {p.estoqueAtual}
                                                </td>
                                                <td className="text-center">
                                                    <Link to={`/ecommerce/${p.codigo}`} className="btn btn-sm btn-primary">
                                                        Ver
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className={styles.productGrid}>
                                {produtos.map(produto => (
                                    <EntradaCard key={produto.codigo} produto={produto} />
                                ))}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default RelatorioEntradasEcommerce;
